/**
 * Combined billing on the Billing details card (0215, a couple on one
 * invoice). A couple who share one QuickBooks customer get one invoice:
 * the PAYER holds it, and each PARTNER's work goes on it, each line
 * saying whose care it was.
 *
 *   - A partner's card says whose invoice its work goes on, and when it
 *     still holds a QuickBooks customer of its own, that it is not used
 *     meanwhile.
 *   - A payer's card lists whose work goes on its invoice.
 *   - Anyone else's card says nothing about couples: the office sees the
 *     "Combine with another client" button, everyone else sees nothing.
 *
 * Only the office chooses who receives the bill: set_bill_to seats
 * get Change / Remove (house Buttons) and the dialog (house
 * ClientLookupField). A refusal reads as its own sentence from
 * errorCopy.ts (billingHouseholdText), never a server message.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/primitives/Button';
import { Dialog } from '@/components/primitives/Dialog';
import { ClientLookupField, type ClientLookupPick } from '@/components/aiTools/ClientLookupField';
import { useTenant } from '@/lib/auth';
import { canSetBillTo, canViewAllClinical } from '@/lib/capabilities';
import { billingHouseholdText, pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import {
  billingHouseholdKey,
  putBillingHousehold,
  useBillingHousehold,
  type BillingHouseholdWire,
} from '@/lib/billingHousehold';

export interface BillingHouseholdLineProps {
  tenantSlug: string;
  clientId: string;
  /** For the sentences (already on the page). */
  firstName: string;
}

export function BillingHouseholdLine({ tenantSlug, clientId, firstName }: BillingHouseholdLineProps): ReactNode {
  const { whoami } = useTenant();
  const contact = useSupportContact();
  const queryClient = useQueryClient();
  const canManage = canSetBillTo(whoami);
  const { data } = useBillingHousehold(tenantSlug, clientId);
  const [choosing, setChoosing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The payer before the change travels with the change: React Query hands
  // a mutation its callbacks in an effect, so onSuccess reading this
  // render's household could see a later read's.
  const saveMut = useMutation<BillingHouseholdWire, unknown, { target: string | null; before: string | null }>({
    mutationFn: ({ target }) => putBillingHousehold(tenantSlug, clientId, target),
    onSuccess: (next, { target, before }) => {
      queryClient.setQueryData(billingHouseholdKey(tenantSlug, clientId), next);
      // The payer's card lists its partners: both payers' cards move.
      for (const id of [before, target]) {
        if (id !== null) void queryClient.invalidateQueries({ queryKey: billingHouseholdKey(tenantSlug, id) });
      }
      void queryClient.invalidateQueries({ queryKey: ['invoices', 'unbilled', tenantSlug] });
      setError(null);
      setChoosing(false);
    },
    onError: (e) => setError(billingHouseholdText(e, contact) ?? pageWriteText(e, contact)),
  });

  // Nothing to say until the read lands, and nothing from an answer that is
  // not the household's shape (an older server): the card stands without it.
  if (data === undefined || !Array.isArray(data.billed_with_me)) return null;
  const payer = data.bills_with;
  // A client not in a couple: nothing to say, and nothing to show a seat
  // that cannot combine.
  if (payer === null && data.billed_with_me.length === 0 && !canManage) return null;

  return (
    <div className="mt-3 space-y-1.5 border-t border-sage-800/[8%] pt-3 text-sm" data-testid="billing-household-line">
      {payer !== null ? (
        <p className="text-ink">
          {`Combined with ${payer.display_name}: ${firstName}'s work goes on ${payer.display_name}'s invoice.`}
        </p>
      ) : data.billed_with_me.length > 0 ? (
        <p className="text-ink">
          {`Combined with ${data.billed_with_me.map((p) => p.display_name).join(', ')}: their work goes on ${firstName}'s invoice.`}
        </p>
      ) : null}
      {payer !== null && data.qbo_link_unused && (
        <p className="text-xs text-ink-soft" data-testid="billing-household-unused-qbo-link">
          {`${firstName}'s own QuickBooks customer isn't used while ${firstName}'s work goes on ${payer.display_name}'s invoice.`}
        </p>
      )}
      {canManage && data.billed_with_me.length === 0 && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="ghost"
            size="sm"
            data-testid="billing-household-change"
            onClick={() => {
              setError(null);
              setChoosing(true);
            }}
          >
            {payer !== null ? 'Change' : 'Combine with another client'}
          </Button>
          {payer !== null && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="billing-household-remove"
              disabled={saveMut.isPending}
              onClick={() => saveMut.mutate({ target: null, before: payer?.client_id ?? null })}
            >
              Bill separately
            </Button>
          )}
        </div>
      )}
      {error !== null && !choosing && (
        <p role="alert" className="text-xs text-pepper-deep" data-testid="billing-household-error">
          {error}
        </p>
      )}
      {choosing && (
        <Dialog
          title="Combine with another client"
          size="md"
          testId="billing-household-dialog"
          onClose={() => setChoosing(false)}
          footer={
            <Button variant="ghost" size="sm" onClick={() => setChoosing(false)}>
              Cancel
            </Button>
          }
        >
          <div className="space-y-3 py-2">
            <p className="text-sm text-ink-soft">
              {`Choose the client whose invoice ${firstName}'s work goes on. Each line on that invoice says whose care it was.`}
            </p>
            <ClientLookupField
              tenantSlug={tenantSlug}
              canLookup={canViewAllClinical(whoami)}
              onPick={(picked: ClientLookupPick) => saveMut.mutate({ target: picked.id, before: payer?.client_id ?? null })}
              disabled={saveMut.isPending}
              testIdPrefix="billing-household"
              label="Client"
            />
            {error !== null && (
              <p role="alert" className="text-xs text-pepper-deep" data-testid="billing-household-dialog-error">
                {error}
              </p>
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
}
